import { FormSectionHandler } from '../entity.handler';
import { AssetStateProfileResponse, AssetStateProfileUpdateRequest, AssetStateItem } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, EventFilterField, NumberField } from '../utils';
import { ICONS } from '../icons';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import * as uuid from 'uuid';
import { EventFilterService } from 'app/services/events/event-filter.service';

interface EditorValues {
    name?: string;
    items?: ListFieldValue<AssetStateItem>[];
}

export class AssetStateProfileDetailsSectionHandler implements FormSectionHandler<AssetStateProfileResponse, EditorValues> {

    type = 'form';
    icon = ICONS.assetstateprofile;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');
    warning = null;

    constructor(
        public admin: AdminEditorService,
        private filters: EventFilterService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSETSTATEPROFILE.DETAILS.${key}`, values);
    }

    applyUpdate(entity: AssetStateProfileResponse, payload: AssetStateProfileUpdateRequest): Promise<AssetStateProfileResponse> {
        return this.admin.applyUpdate(api => api.entities.updateAssetStateProfile(entity.id, payload));
    }

    mapEntityToEditor(entity: AssetStateProfileResponse): Promise<EditorValues> {

        this.warning = entity.items && entity.items.length > 1 ? null : this.admin.translate(`EDITORS.ASSETSTATEPROFILE.WARNINGS.ITEM_LENGTH`);

        const result = {
            name: entity.name,
            items: (entity.items && entity.items.map(x => this.stateItemToListItem(x))) || [],
        };
        Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // strip undefined properties from the object
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: AssetStateProfileUpdateRequest, values: EditorValues): Promise<AssetStateProfileUpdateRequest> {
        const patch: AssetStateProfileUpdateRequest = {
            name: values.name,
            items: (values.items && values.items.map(x => x.data)) || undefined,
        };
        Object.keys(patch).forEach(key => patch[key] === undefined && delete patch[key]); // strip undefined properties from the object
        return Promise.resolve(EntityUtils.mergeFields(['name', 'items'], patch, entity));
    }

    getFields(entity: AssetStateProfileResponse): Promise<FormBuilderField[]> {
        return Promise.resolve().then(() => {
            return [
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                {
                    id: 'items', title: this.translate('FIELDS.STATES'), type: 'list', required: false, visible: !this.creating,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showFilterModal.bind(this), },
                        { id: 'edit', click: this.showFilterModal.bind(this), },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
                },
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));

    }

    stateItemToListItem(item: AssetStateItem): ListFieldValue<AssetStateItem> {
        return {
            id: item.id,
            name: item.state,
            icon: 'label',
            color: item.color,
            description: item.name,
            data: item,
        };
    }

    updateOrAddItem(items: ListFieldValue<AssetStateItem>[], existingItem: ListFieldValue<AssetStateItem>, item: AssetStateItem) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = this.stateItemToListItem(item);
        } else {
            items.push(this.stateItemToListItem(item));
        }
    }

    showFilterModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<AssetStateItem> {
        return this.admin.showFormModal<AssetStateItem>({
            groups: [{
                name: this.translate('MODALS.STATE.TITLE'),
                description: this.translate('MODALS.STATE.DESC'),
                fields: [
                    TextField('state', this.translate('FIELDS.STATE'), true, 1, 255),
                    { id: 'color', title: this.translate('FIELDS.COLOR'), type: 'color', required: true },
                    { ...NumberField('radius', this.translate('FIELDS.RADIUS'), false, 0, 1000, this.translate('FIELDS.RADIUS_UNIT')), description: this.translate('FIELDS.RADIUS_DESC') },
                    EventFilterField('filter', this.translate('FIELDS.FILTER'), true, this.ownerId, false, this.filters),

                ],
            }],
        }, (item && item.data) || { // pass some defaults for new items
            id: uuid.v4(),
            name: '',
            state: '',
            color: '#0000ff',
            radius: 0,
            filter: null,
        }).then(result => {
            if (result) {
                result.name = this.filters.getEventFilterText(result.filter);
                this.updateOrAddItem(values.items, item, result);
            }
            return result;
        });



    }



}
